import logo from "@/assets/logo-mark-256.webp";
import { COMPANY, BRANCHES } from "@/lib/company";
import { getActiveProvince } from "@/provinces/_registry";
// Link-only projection: the footer renders on every page, so it must never
// pull article excerpts/descriptions into the client entry bundle.
import { INSIGHT_LINKS as INSIGHTS } from "@/content/insights/links.gen";

export function SiteFooter() {
  const { config } = getActiveProvince();
  const branches = config.branches.length > 0 ? config.branches : BRANCHES;
  const districtList = config.allDistricts.join(" ");
  const landmarkList = config.landmarks.slice(0, 9).join(" ");
  // Sitewide cross-links to boost inbound link density for low-linked leaf pages
  const featuredAreas = [
    "talad-yai",
    "thep-krasattri",
    "cape-panwa",
    "koh-naka",
    "phuket-old-town",
    "patong",
    "cherngtalay",
    "rawai",
  ];
  return (
    <footer className="border-t border-border bg-primary pb-[calc(5.5rem+env(safe-area-inset-bottom))] text-primary-foreground md:pb-0">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 md:grid-cols-4 lg:grid-cols-6">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img src={logo} alt={COMPANY.brandName} className="h-12 w-12" width="48" height="48" loading="lazy" decoding="async" />
            <div>
              <div className="text-base font-extrabold">{COMPANY.legalName}</div>
              <div className="text-xs uppercase tracking-widest text-gold">เลขทะเบียน {COMPANY.registrationNumber}</div>
            </div>
          </div>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-primary-foreground/90">
            สำนักงานทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ประจำจังหวัด{config.nameTh}
            ขึ้นทะเบียนกับสภาทนายความแห่งประเทศไทย ให้บริการ Notary Public, แปล NAATI/MoJ, รับรองกงสุลกิตติมศักดิ์ใน{config.nameTh},
            ประสาน MFA และสถานทูต รวมถึงวีซ่า LTR / DTV / Retirement / Marriage สำหรับ Expat และนักลงทุนต่างชาติ
            ครอบคลุม {config.districtCount} อำเภอทั่ว{config.nameTh} — {districtList} • {landmarkList}
          </p>
          <div className="mt-5 space-y-1 text-sm text-primary-foreground/90">
            <div>📞 Call Center: <a href={`tel:${COMPANY.phoneIntl}`} className="font-bold text-gold hover:underline">{COMPANY.phone}</a></div>
            <div>💬 LINE ID: <span className="font-bold text-gold">{COMPANY.lineId}</span></div>
            <div>✉ {COMPANY.email}</div>
          </div>
        </div>
        <div>
          <h4 className="text-sm font-bold text-gold">จุดบริการใน{config.nameTh}</h4>
          <ul className="mt-3 space-y-3 text-xs text-primary-foreground/95">
            {branches.map((b) => (
              <li key={b.id}>
                <div className="font-semibold text-primary-foreground">{b.name}</div>
                <div className="leading-snug">{b.address}</div>
                <a href={b.mapUrl} target="_blank" rel="noopener" className="text-gold hover:underline">→ Google Maps</a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-bold text-gold">บทความล่าสุด</h4>
          <ul className="mt-3 space-y-2 text-xs text-primary-foreground/95">
            {INSIGHTS.map((p) => (
              <li key={p.slug}>
                <a href={`/insights/${p.slug}`} className="hover:text-gold hover:underline">
                  {p.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-bold text-gold">พื้นที่ยอดนิยม</h4>
          <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-primary-foreground/95">
            {featuredAreas.map((s) => (
              <li key={s}>
                <a href={`/areas/${s}`} className="hover:text-gold hover:underline">
                  {s.replace(/-/g, " ")}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-bold text-gold">เว็บไซต์ในเครือ</h4>
          <ul className="mt-3 space-y-2 text-sm text-primary-foreground/95">
            {COMPANY.websites.map((w) => (
              <li key={w}><a href={w} target="_blank" rel="noopener" className="hover:underline">{w.replace("https://", "")}</a></li>
            ))}
          </ul>
          <h4 className="mt-6 text-sm font-bold text-gold">บริการยอดนิยม</h4>
          <ul className="mt-3 space-y-1.5 text-xs text-primary-foreground/95">
            <li><a href="https://thainotary.co.th/notary-public" className="hover:underline">Notary Public</a></li>
            <li><a href="https://thainotary.co.th/translation/naati" className="hover:underline">NAATI Translation</a></li>
            <li><a href="https://thainotary.co.th/apostille" className="hover:underline">MFA / Apostille</a></li>
            <li><a href="https://thainotary.co.th/embassy-legalization" className="hover:underline">Embassy Legalization</a></li>
            <li><a href="https://thainotary.co.th/criminal-record-check" className="hover:underline">Police Clearance</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-4 text-center text-xs text-primary-foreground/85">
        <div className="mb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <a href="/about" className="hover:text-gold hover:underline">เกี่ยวกับเรา</a>
          <a href="/services" className="hover:text-gold hover:underline">บริการ</a>
          <a href="/catalog" className="hover:text-gold hover:underline">แคตตาล็อกบริการทั้งหมด</a>
          <a href="/en/catalog" className="hover:text-gold hover:underline">Service Catalog (EN)</a>
          <a href="/pricing" className="hover:text-gold hover:underline">ราคา</a>
          <a href="/pricing-matrix" className="hover:text-gold hover:underline">ตารางราคา</a>
          <a href="/tools" className="hover:text-gold hover:underline">เครื่องมือคำนวณ</a>
          <a href="/recommend" className="hover:text-gold hover:underline">แนะนำบริการ</a>
          <a href="/visa/faq" className="hover:text-gold hover:underline">FAQ วีซ่า/ตม.</a>
          <a href="/visa/thai" className="hover:text-gold hover:underline">วีซ่าไทยทุกประเภท</a>
          <a href="/work-permit/type" className="hover:text-gold hover:underline">ใบอนุญาตทำงาน</a>
          <a href="/work-permit/faq" className="hover:text-gold hover:underline">FAQ ใบอนุญาตทำงาน</a>
          <a href="/company/faq" className="hover:text-gold hover:underline">FAQ จดทะเบียนบริษัท</a>
          <a href="/company/type" className="hover:text-gold hover:underline">รูปแบบนิติบุคคล</a>
          <a href="/marriage/faq" className="hover:text-gold hover:underline">FAQ จดทะเบียนสมรส</a>
          <a href="/property/faq" className="hover:text-gold hover:underline">FAQ ซื้ออสังหาฯ/โอนกรรมสิทธิ์</a>
          <a href="/inheritance/faq" className="hover:text-gold hover:underline">FAQ พินัยกรรม/มรดก</a>
          <a href="/education/faq" className="hover:text-gold hover:underline">FAQ รับรองวุฒิการศึกษา</a>
          <a href="/driving/faq" className="hover:text-gold hover:underline">FAQ ใบขับขี่ไทย/สากล</a>
          <a href="/medical/faq" className="hover:text-gold hover:underline">FAQ เอกสารการแพทย์/ประกันสุขภาพ</a>
          <a href="/civil-registry/faq" className="hover:text-gold hover:underline">FAQ ทะเบียนราษฎร/สูติบัตร/ทะเบียนบ้าน</a>
          <a href="/corporate-docs/faq" className="hover:text-gold hover:underline">FAQ เอกสารบริษัท/มอบอำนาจข้ามประเทศ</a>
          <a href="/interpreter/faq" className="hover:text-gold hover:underline">FAQ ล่ามราชการ/ล่ามศาล</a>
          <a href="/employment/faq" className="hover:text-gold hover:underline">FAQ เอกสารสมัครงาน/ทุนต่างประเทศ</a>
          <a href="/insights" className="hover:text-gold hover:underline">บทความ</a>
          <a href="/case-studies" className="hover:text-gold hover:underline">เคสจริง</a>
          <a href="/glossary" className="hover:text-gold hover:underline">อภิธานศัพท์</a>
          <a href="/compare" className="hover:text-gold hover:underline">เปรียบเทียบ</a>
          <a href="/howto" className="hover:text-gold hover:underline">คู่มือ How-To</a>
          <a href="/business/faq" className="hover:text-gold hover:underline">FAQ ธุรกิจ &amp; BOI</a>
          <a href="/notary/faq" className="hover:text-gold hover:underline">FAQ โนตารี</a>
          <a href="/translation/faq" className="hover:text-gold hover:underline">FAQ แปลเอกสาร</a>
          <a href="/consular-legalization/faq" className="hover:text-gold hover:underline">FAQ รับรองกงสุล</a>
          <a href="/police-clearance/faq" className="hover:text-gold hover:underline">FAQ ตรวจประวัติ</a>
          <a href="/embassies" className="hover:text-gold hover:underline">สถานทูต & กงสุล</a>
          <a href="/bangkok" className="hover:text-gold hover:underline">กรุงเทพ 50 เขต</a>
          <a href="/provinces" className="hover:text-gold hover:underline">บริการ 77 จังหวัด</a>
          <a href="/notary" className="hover:text-gold hover:underline">ศูนย์รวมโนตารีพับลิค</a>
          <a href="/translation" className="hover:text-gold hover:underline">ศูนย์รวมแปลเอกสารรับรอง</a>
          <a href="/near" className="hover:text-gold hover:underline">ค้นหาบริการใกล้ฉัน</a>
          <a href="/countries" className="hover:text-gold hover:underline">ประเทศปลายทาง</a>
          <a href="/areas" className="hover:text-gold hover:underline">พื้นที่ให้บริการภูเก็ต</a>
          <a href="/locations" className="hover:text-gold hover:underline">สำนักงาน &amp; จุดนัดพบ</a>
          <a href="/for" className="hover:text-gold hover:underline">บริการตามกลุ่มลูกค้า</a>
          <a href="/naati/faq" className="hover:text-gold hover:underline">FAQ แปล NAATI</a>
          <a href="/legal/faq" className="hover:text-gold hover:underline">FAQ กฎหมาย/คดีความ</a>
          <a href="/poa/faq" className="hover:text-gold hover:underline">FAQ หนังสือมอบอำนาจ</a>
          <a href="/ip/faq" className="hover:text-gold hover:underline">FAQ เครื่องหมายการค้า/IP</a>
          <a href="/tax/faq" className="hover:text-gold hover:underline">FAQ ภาษี/ผู้มีถิ่นที่อยู่</a>
          <a href="/pension/faq" className="hover:text-gold hover:underline">FAQ บำนาญ/ประกันสังคม</a>
          <a href="/insurance/faq" className="hover:text-gold hover:underline">FAQ ประกันภัย</a>
          <a href="/vehicle/faq" className="hover:text-gold hover:underline">FAQ ทะเบียนรถ/นำรถเข้า</a>
          <a href="/relocation/faq" className="hover:text-gold hover:underline">FAQ ย้ายถิ่นฐาน</a>
          <a href="/en" className="hover:text-gold hover:underline">English site</a>
          <a href="/en/services" className="hover:text-gold hover:underline">Services (EN)</a>
          <a href="/en/notary" className="hover:text-gold hover:underline">Notary Public (EN)</a>
          <a href="/en/naati" className="hover:text-gold hover:underline">NAATI Translation (EN)</a>
          <a href="/en/police-clearance" className="hover:text-gold hover:underline">Police Clearance (EN)</a>
          <a href="/en/insights" className="hover:text-gold hover:underline">Insights (EN)</a>
          <a href="/industrial-estates" className="hover:text-gold hover:underline">นิคมอุตสาหกรรม (B2B)</a>
          <a href="/reviews" className="hover:text-gold hover:underline">รีวิว</a>
          <a href="/contact" className="hover:text-gold hover:underline">ติดต่อ</a>
          <a href="/sitemap-html" className="hover:text-gold hover:underline">แผนผังเว็บ</a>
          <a href="/privacy" className="hover:text-gold hover:underline">PDPA</a>
          <a href="/terms" className="hover:text-gold hover:underline">ข้อตกลง</a>
        </div>
        © {new Date().getFullYear()} {COMPANY.legalName} — All rights reserved. ทะเบียนเลขที่ {COMPANY.registrationNumber}
      </div>
    </footer>
  );
}
